import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import {
    Menu,
    X,
    ChevronDown,
    Phone,
    Facebook,
    Instagram,
    MapPin,
    Globe,
    Mail,
    Star,
} from "lucide-react";
import { SOCIAL } from "../data/siteData";
import { useSettings } from "../context/SettingsContext";
import { usePagePhone } from "../hooks/usePagePhone";
import Logo from "./Logo";

const navItems = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/courses", label: "Courses" },
    { to: "/online-classes", label: "Online Classes" },
    // V81 — "Makeover Classes" removed from top nav (still linked from Courses
    // page). Page /online-makeover-classes stays live, indexable, in sitemap.
    // Rationale: reduce header overflow so Pay Now button is fully visible.
    { to: "/online-tuition-worldwide", label: "Worldwide" },
    { to: "/foreign-students", label: "Foreign Students" },
    // V91 — "Tutor Guide" removed from header nav (available in the footer).
    // V92 — "Blog" and "FAQs" also removed from the header nav (available in
    // the footer). Pages /blog, /faqs stay live, indexable, sitemap-listed.
    // Together this trims 3 slots from the desktop header for a cleaner,
    // conversion-focused layout. Mobile drawer keeps ALL items intact.
    { to: "/contact", label: "Contact" },
];

// V91/V92 — Mobile drawer keeps the full menu including Tutor Guide, Blog
// and FAQs so mobile users retain the same navigation surface. Only the
// desktop header is decluttered; every URL remains reachable via footer,
// sitemap, and organic search.
const mobileNavItems = [
    ...navItems.slice(0, -1), // everything except the trailing "Contact"
    { to: "/blog", label: "Blog" },
    { to: "/faqs", label: "FAQs" },
    { to: "/tutor-guide", label: "📘 Tutor Guide" },
    navItems[navItems.length - 1], // "Contact" stays last
];

const HQ_CITIES = [
    { to: "/jabalpur", label: "Jabalpur", badge: "HQ" },
    { to: "/bhopal", label: "Bhopal", badge: null },
    { to: "/nagpur", label: "Nagpur", badge: null },
];

const OTHER_CITIES = [
    { to: "/home-tuition-mumbai", label: "Mumbai (Online)" },
    { to: "/home-tuition-delhi", label: "Delhi NCR (Online)" },
    { to: "/home-tuition-bangalore", label: "Bangalore (Online)" },
    { to: "/home-tuition-pune", label: "Pune (Online)" },
    { to: "/home-tuition-hyderabad", label: "Hyderabad (Online)" },
    { to: "/home-tuition-chennai", label: "Chennai (Online)" },
    { to: "/home-tuition-kolkata", label: "Kolkata (Online)" },
    { to: "/home-tuition-ahmedabad", label: "Ahmedabad (Online)" },
    { to: "/indore", label: "Indore (Online)" },
];

// Combined list for the desktop dropdown (HQ first).
const cityLinks = [
    ...HQ_CITIES.map((c) => ({ to: c.to, label: c.badge ? `${c.label} (${c.badge})` : c.label })),
    ...OTHER_CITIES,
];

const countryLinks = [
    { to: "/online-tuition-worldwide", label: "🌍 All Countries", flag: "🌐" },
    { to: "/online-tuition-usa", label: "USA", flag: "🇺🇸" },
    { to: "/online-tuition-uk", label: "UK", flag: "🇬🇧" },
    { to: "/online-tuition-canada", label: "Canada", flag: "🇨🇦" },
    { to: "/online-tuition-australia", label: "Australia", flag: "🇦🇺" },
    { to: "/online-tuition-new-zealand", label: "New Zealand", flag: "🇳🇿" },
    { to: "/online-tuition-uae", label: "UAE", flag: "🇦🇪" },
    { to: "/online-tuition-saudi-arabia", label: "Saudi Arabia", flag: "🇸🇦" },
    { to: "/online-tuition-qatar", label: "Qatar", flag: "🇶🇦" },
    { to: "/online-tuition-kuwait", label: "Kuwait", flag: "🇰🇼" },
    { to: "/online-tuition-oman", label: "Oman", flag: "🇴🇲" },
    { to: "/online-tuition-bahrain", label: "Bahrain", flag: "🇧🇭" },
    { to: "/online-tuition-singapore", label: "Singapore", flag: "🇸🇬" },
    { to: "/online-tuition-malaysia", label: "Malaysia", flag: "🇲🇾" },
    { to: "/online-tuition-japan", label: "Japan", flag: "🇯🇵" },
    { to: "/online-tuition-south-korea", label: "South Korea", flag: "🇰🇷" },
    { to: "/online-tuition-germany", label: "Germany", flag: "🇩🇪" },
    { to: "/online-tuition-france", label: "France", flag: "🇫🇷" },
    { to: "/online-tuition-netherlands", label: "Netherlands", flag: "🇳🇱" },
    { to: "/online-tuition-ireland", label: "Ireland", flag: "🇮🇪" },
    { to: "/online-tuition-switzerland", label: "Switzerland", flag: "🇨🇭" },
    { to: "/online-tuition-belgium", label: "Belgium", flag: "🇧🇪" },
    { to: "/online-tuition-italy", label: "Italy", flag: "🇮🇹" },
    { to: "/online-tuition-spain", label: "Spain", flag: "🇪🇸" },
    { to: "/online-tuition-norway", label: "Norway", flag: "🇳🇴" },
    { to: "/online-tuition-sweden", label: "Sweden", flag: "🇸🇪" },
    { to: "/online-tuition-finland", label: "Finland", flag: "🇫🇮" },
    { to: "/online-tuition-denmark", label: "Denmark", flag: "🇩🇰" },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "");

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [cityOpen, setCityOpen] = useState(false);
    const [countryOpen, setCountryOpen] = useState(false);
    const [mCitiesOpen, setMCitiesOpen] = useState(true); // V48 — accordion, expanded by default
    const [mCountriesOpen, setMCountriesOpen] = useState(false);
    const { settings } = useSettings();
    const fbUrl = settings.facebook_url || SOCIAL.facebook;
    const igUrl = settings.instagram_url || SOCIAL.instagram;
    const pagePhone = usePagePhone();

    // Lock body scroll + expose menu-open state to floating widgets via CSS.
    // V88 — Desktop-Site-on-mobile fix: `data-menu-open` lets the app hide
    // floating buttons (WhatsApp / Free Consultation / Bottom CTA bar) while
    // the drawer is open, and lets us switch to 100dvh sizing safely.
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
            document.body.dataset.menuOpen = "true";
        } else {
            document.body.style.overflow = "";
            delete document.body.dataset.menuOpen;
        }
        return () => {
            document.body.style.overflow = "";
            delete document.body.dataset.menuOpen;
        };
    }, [open]);

    const closeMenu = () => setOpen(false);

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200"
        >
            {/* Top utility bar (desktop only) */}
            <div className="hidden md:block bg-[#0F172A] text-white text-xs">
                <div className="max-w-[1440px] mx-auto px-6 py-2 flex items-center justify-between">
                    <span className="tracking-wide">12+ Years · 50,000+ Students · 2,500+ Verified Tutors</span>
                    <div className="flex items-center gap-5">
                        <a href={`tel:${pagePhone.tel}`} className="hover:text-amber-400 inline-flex items-center gap-1.5" data-testid="header-phone" data-phone-key={pagePhone.key}>
                            <Phone className="w-3.5 h-3.5" /> Call Us
                        </a>
                        <span className="inline-flex items-center gap-1.5" data-testid="header-email-group">
                            <a href="mailto:anandhometutorials@gmail.com" className="hover:text-amber-400" data-testid="header-email">
                                anandhometutorials@gmail.com
                            </a>
                            <span className="opacity-40">·</span>
                            <a href="mailto:info@anandtutorials.com" className="hover:text-amber-400" data-testid="header-email-business">
                                info@anandtutorials.com
                            </a>
                        </span>
                        <div className="flex items-center gap-2 pl-3 border-l border-white/10">
                            {fbUrl && (
                                <a href={fbUrl} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="header-facebook" className="hover:text-amber-400">
                                    <Facebook className="w-3.5 h-3.5" />
                                </a>
                            )}
                            {igUrl && (
                                <a href={igUrl} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="header-instagram" className="hover:text-amber-400">
                                    <Instagram className="w-3.5 h-3.5" />
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main header row.
                V87 — Compressed padding + gaps at the xl breakpoint (1280-1535)
                so 1366×768 / 1440×900 laptops keep the whole header on ONE
                horizontal line. 2xl (1536+) keeps the original comfortable
                spacing. No functional / structural / SEO changes. */}
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-6 2xl:px-8 py-2 sm:py-3 flex items-center justify-between gap-2 xl:gap-3">
                <Link to="/" className="flex items-center shrink-0" data-testid="logo-link" onClick={closeMenu}>
                    <Logo
                        alt="Anand Tutorials — 1:1 Home & Online Tuition"
                        priority
                        className="h-14 sm:h-16 xl:h-12 2xl:h-16 w-auto"
                    />
                </Link>

                <nav className="hidden xl:flex items-center gap-0 2xl:gap-1 flex-nowrap min-w-0">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-${slug(item.label)}`}
                            className={({ isActive }) =>
                                `px-1.5 2xl:px-3 py-2 rounded-md text-[13px] 2xl:text-sm font-medium whitespace-nowrap transition-colors ${
                                    isActive ? "text-[#1E3A8A] bg-blue-50" : "text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-50"
                                }`
                            }
                            end={item.to === "/"}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    <div
                        className="relative"
                        onMouseEnter={() => setCityOpen(true)}
                        onMouseLeave={() => setCityOpen(false)}
                    >
                        <button
                            className="px-1.5 2xl:px-3 py-2 rounded-md text-[13px] 2xl:text-sm font-medium whitespace-nowrap text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-50 inline-flex items-center gap-0.5 2xl:gap-1"
                            data-testid="nav-cities-trigger"
                            aria-haspopup="menu"
                            aria-expanded={cityOpen}
                        >
                            Cities <ChevronDown className={`w-4 h-4 transition-transform ${cityOpen ? "rotate-180" : ""}`} />
                        </button>
                        {cityOpen && (
                            <div
                                role="menu"
                                data-testid="nav-cities-panel"
                                className="absolute top-full left-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl p-2 z-[45] animate-nav-dropdown"
                            >
                                {cityLinks.map((c) => (
                                    <Link
                                        key={c.to}
                                        to={c.to}
                                        role="menuitem"
                                        data-testid={`nav-city-${slug(c.label)}`}
                                        className="block px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-[#1E3A8A] rounded-md transition-colors"
                                    >
                                        {c.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                    <div
                        className="relative"
                        onMouseEnter={() => setCountryOpen(true)}
                        onMouseLeave={() => setCountryOpen(false)}
                    >
                        <button
                            className="px-1.5 2xl:px-3 py-2 rounded-md text-[13px] 2xl:text-sm font-medium whitespace-nowrap text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-50 inline-flex items-center gap-0.5 2xl:gap-1"
                            data-testid="nav-countries-trigger"
                            aria-haspopup="menu"
                            aria-expanded={countryOpen}
                        >
                            Countries <ChevronDown className={`w-4 h-4 transition-transform ${countryOpen ? "rotate-180" : ""}`} />
                        </button>
                        {countryOpen && (
                            <div
                                role="menu"
                                data-testid="nav-countries-panel"
                                className="absolute top-full right-0 mt-2 w-64 max-h-[520px] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl p-2 z-[45] animate-nav-dropdown"
                            >
                                {countryLinks.map((c) => (
                                    <Link
                                        key={c.to}
                                        to={c.to}
                                        role="menuitem"
                                        data-testid={`nav-country-${slug(c.label)}`}
                                        className="block px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-[#1E3A8A] rounded-md transition-colors"
                                    >
                                        <span className="mr-2">{c.flag}</span>{c.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </nav>

                {/* Right cluster — CTAs + mobile toggle.
                    V91 — Vertical divider + generous margin-left at xl separates
                    the CTAs from the Countries dropdown trigger, so the
                    dropdown menu's rightward extent can never visually collide
                    with the CTA area. CTAs stay above dropdowns via z-[50]. */}
                <div className="flex items-center gap-1.5 sm:gap-2 2xl:gap-3 shrink-0 xl:ml-3 2xl:ml-5 xl:pl-3 2xl:pl-5 xl:border-l xl:border-slate-200/70 relative z-[50]">
                    <Link
                        to="/register"
                        data-testid="cta-book-demo"
                        className="hidden sm:inline-flex items-center bg-[#1E3A8A] text-white px-2.5 xl:px-3 2xl:px-4 py-2 rounded-lg text-xs xl:text-[13px] 2xl:text-sm font-semibold shadow-sm shadow-blue-900/10 hover:bg-[#1E40AF] hover:shadow-md transition-all whitespace-nowrap"
                    >
                        Book Free Demo
                    </Link>
                    <Link
                        to="/pay-now"
                        data-testid="cta-pay-now"
                        className="hidden md:inline-flex items-center bg-amber-500 text-white px-3 2xl:px-4 py-2 rounded-lg text-[13px] 2xl:text-sm font-semibold shadow-sm shadow-amber-900/10 hover:bg-amber-600 hover:shadow-md transition-all whitespace-nowrap"
                    >
                        Pay Now
                    </Link>
                    <button
                        className="xl:hidden p-2 -mr-1 rounded-lg text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                        onClick={() => setOpen(!open)}
                        data-testid="mobile-menu-toggle"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile menu — full-viewport backdrop + sheet.
                V88 — Fullscreen sheet (top-0 → 100dvh) + z-[70] above the
                sticky header (z-50). Fixes Chrome "Desktop Site" mode on
                mobile: previously the drawer was capped to `calc(100vh-80px)`
                and offset by a hard-coded header height, cropping the bottom
                and misaligning at wider layout viewports. */}
            <div
                className={`xl:hidden fixed inset-0 z-[70] transition-opacity duration-300 ${
                    open ? "opacity-100 pointer-events-auto visible" : "opacity-0 pointer-events-none invisible"
                }`}
                aria-hidden={!open}
            >
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                    onClick={closeMenu}
                    data-testid="mobile-menu-backdrop"
                />
                {/* Sheet — uses 100dvh so Chrome/Safari Desktop-Site mode
                    doesn't clip the bottom, and stays a flex column so the
                    sticky mini-header + scrollable body play nicely. */}
                <div
                    className={`absolute inset-x-0 top-0 bg-white shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
                        open ? "translate-y-0" : "-translate-y-full"
                    }`}
                    style={{ height: "100dvh", maxHeight: "100dvh" }}
                    data-testid="mobile-menu"
                >
                    {/* Persistent header bar with logo + close button — always
                        visible even when the body below is scrolled. */}
                    <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-slate-200">
                        <Link to="/" onClick={closeMenu} className="flex items-center" data-testid="mobile-menu-logo">
                            <Logo alt="Anand Tutorials" className="h-10 w-auto" />
                        </Link>
                        <button
                            type="button"
                            onClick={closeMenu}
                            aria-label="Close menu"
                            data-testid="mobile-menu-close"
                            className="p-2 rounded-lg text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    {/* Scrollable body */}
                    <div
                        className="flex-1 overflow-y-auto overscroll-contain"
                        style={{ WebkitOverflowScrolling: "touch" }}
                    >
                    {/* Quick CTAs */}
                    <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50/80 border-b border-slate-100">
                        <Link
                            to="/register"
                            onClick={closeMenu}
                            className="flex items-center justify-center gap-1.5 bg-[#1E3A8A] text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-[#1E40AF] active:scale-95 transition-all"
                            data-testid="mobile-cta-demo-top"
                        >
                            <Star className="w-4 h-4" /> Book Free Demo
                        </Link>
                        <Link
                            to="/pay-now"
                            onClick={closeMenu}
                            className="flex items-center justify-center bg-amber-500 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-amber-600 active:scale-95 transition-all"
                            data-testid="mobile-cta-pay-top"
                        >
                            Pay Now
                        </Link>
                    </div>

                    {/* Primary nav (compact) */}
                    <nav className="px-3 py-2 flex flex-col">
                        {mobileNavItems.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                onClick={closeMenu}
                                className="flex items-center px-2 py-2.5 text-[15px] text-slate-800 font-medium rounded-md hover:bg-slate-50 active:bg-slate-100 transition-colors"
                                data-testid={`mobile-nav-${slug(item.label)}`}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Cities accordion */}
                    <div className="px-3 border-t border-slate-100">
                        <button
                            onClick={() => setMCitiesOpen((v) => !v)}
                            className="w-full flex items-center justify-between px-2 py-3 text-[13px] uppercase tracking-wider text-slate-500 font-semibold"
                            data-testid="mobile-cities-toggle"
                            aria-expanded={mCitiesOpen}
                        >
                            <span className="flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5" /> Cities
                            </span>
                            <ChevronDown
                                className={`w-4 h-4 transition-transform duration-200 ${mCitiesOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        <div
                            className={`overflow-hidden transition-[max-height] duration-300 ease-out ${
                                mCitiesOpen ? "max-h-[640px]" : "max-h-0"
                            }`}
                        >
                            <div className="pb-2 grid grid-cols-1 gap-0.5">
                                {/* HQ + anchor cities (highlighted) */}
                                {HQ_CITIES.map((c) => (
                                    <Link
                                        key={c.to}
                                        to={c.to}
                                        onClick={closeMenu}
                                        className="flex items-center justify-between px-2 py-2.5 text-[15px] text-[#0F172A] font-semibold rounded-md hover:bg-blue-50 active:bg-blue-100 transition-colors"
                                        data-testid={`mobile-city-${slug(c.label)}`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-[#1E3A8A]" /> {c.label}
                                        </span>
                                        {c.badge && (
                                            <span className="text-[10px] font-bold tracking-wide bg-amber-500 text-white px-1.5 py-0.5 rounded">
                                                {c.badge}
                                            </span>
                                        )}
                                    </Link>
                                ))}
                                <div className="h-px my-1.5 bg-slate-100" />
                                {OTHER_CITIES.map((c) => (
                                    <Link
                                        key={c.to}
                                        to={c.to}
                                        onClick={closeMenu}
                                        className="flex items-center px-2 py-2 text-[14px] text-slate-700 rounded-md hover:bg-slate-50 active:bg-slate-100 transition-colors"
                                        data-testid={`mobile-city-${slug(c.label)}`}
                                    >
                                        <MapPin className="w-3.5 h-3.5 text-slate-400 mr-2" /> {c.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Countries accordion */}
                    <div className="px-3 border-t border-slate-100">
                        <button
                            onClick={() => setMCountriesOpen((v) => !v)}
                            className="w-full flex items-center justify-between px-2 py-3 text-[13px] uppercase tracking-wider text-slate-500 font-semibold"
                            data-testid="mobile-countries-toggle"
                            aria-expanded={mCountriesOpen}
                        >
                            <span className="flex items-center gap-2">
                                <Globe className="w-3.5 h-3.5" /> Countries
                            </span>
                            <ChevronDown
                                className={`w-4 h-4 transition-transform duration-200 ${mCountriesOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        <div
                            className={`overflow-hidden transition-[max-height] duration-300 ease-out ${
                                mCountriesOpen ? "max-h-[1200px]" : "max-h-0"
                            }`}
                        >
                            <div className="pb-2 grid grid-cols-2 gap-1">
                                {countryLinks.map((c) => (
                                    <Link
                                        key={c.to}
                                        to={c.to}
                                        onClick={closeMenu}
                                        className="flex items-center gap-2 px-2 py-2 text-[14px] text-slate-700 rounded-md hover:bg-slate-50 active:bg-slate-100 transition-colors"
                                        data-testid={`mobile-country-${slug(c.label)}`}
                                    >
                                        <span className="text-lg leading-none">{c.flag}</span> {c.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Contact row */}
                    <div className="px-3 py-3 border-t border-slate-100 bg-slate-50/60 rounded-b-2xl">
                        <div className="text-[13px] uppercase tracking-wider text-slate-500 font-semibold mb-2 px-2">
                            Get in Touch
                        </div>
                        <div className="grid grid-cols-2 gap-2 px-1">
                            <a
                                href={`tel:${pagePhone.tel}`}
                                onClick={closeMenu}
                                className="flex items-center gap-2 px-2 py-2 text-[14px] text-[#1E3A8A] font-semibold rounded-md hover:bg-blue-50 active:bg-blue-100 transition-colors"
                                data-testid="mobile-contact-phone"
                            >
                                <Phone className="w-4 h-4" /> Call us
                            </a>
                            <a
                                href="mailto:anandhometutorials@gmail.com"
                                onClick={closeMenu}
                                className="flex items-center gap-2 px-2 py-2 text-[14px] text-[#1E3A8A] font-semibold rounded-md hover:bg-blue-50 active:bg-blue-100 transition-colors"
                                data-testid="mobile-contact-email"
                            >
                                <Mail className="w-4 h-4" /> Email
                            </a>
                        </div>
                        <div className="px-2 pt-2 mt-1 flex flex-col gap-0.5 text-[12px] text-slate-600" data-testid="mobile-contact-email-list">
                            <a
                                href="mailto:anandhometutorials@gmail.com"
                                onClick={closeMenu}
                                className="hover:text-[#1E3A8A] break-all"
                                data-testid="mobile-contact-email-primary"
                            >
                                anandhometutorials@gmail.com
                            </a>
                            <a
                                href="mailto:info@anandtutorials.com"
                                onClick={closeMenu}
                                className="hover:text-[#1E3A8A] break-all"
                                data-testid="mobile-contact-email-business"
                            >
                                info@anandtutorials.com
                            </a>
                        </div>
                        {(fbUrl || igUrl) && (
                            <div className="flex items-center gap-2 px-3 pt-3 mt-2 border-t border-slate-200">
                                <span className="text-[12px] text-slate-500 mr-1">Follow:</span>
                                {fbUrl && (
                                    <a
                                        href={fbUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label="Facebook"
                                        className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100"
                                    >
                                        <Facebook className="w-4 h-4" />
                                    </a>
                                )}
                                {igUrl && (
                                    <a
                                        href={igUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label="Instagram"
                                        className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100"
                                    >
                                        <Instagram className="w-4 h-4" />
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                    </div>{/* /scrollable body */}
                </div>
            </div>
        </header>
    );
}
